/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { dropTask } from 'ember-concurrency';
import AuthService from 'cio/services/auth';
import { UserFlags } from 'cio/models/user';
import AnalyticsService from 'cio/services/analytics';
import { action } from '@ember/object';
import AlertsService from 'cio/services/alerts';

interface ExperimentalUserFlagFeatureUserFlagToggleArgs {
  title: string;
  description: string;
  key: keyof UserFlags;
  disabled?: boolean;
  dependentFlagKey?: keyof UserFlags;
  requiresFeedbackOnDisable?: boolean;
  hideToggle?: boolean;
  parentToggleHidden?: boolean;
}

export default class ExperimentalUserFlagFeatureUserFlagToggle extends Component<ExperimentalUserFlagFeatureUserFlagToggleArgs> {
  @service auth!: AuthService;
  @service analytics!: AnalyticsService;
  @service alerts!: AlertsService;

  @tracked isShowingFeedbackModal = false;

  get flagValue(): boolean {
    return this.auth.user?.userFlags[this.args.key] ?? false;
  }

  get analyticsData() {
    return {
      feature: this.args.key,
    };
  }

  get disabled() {
    return this.toggleFlag.isRunning || this.args.disabled === true;
  }

  get impactDescription() {
    if (this.args.parentToggleHidden === true) {
      return '';
    }
    if (this.args.dependentFlagKey != null && this.auth.user?.userFlags[this.args.dependentFlagKey] !== true) {
      return 'This setting cannot be used if the parent feature is disabled.';
    }

    let introductionText = 'Each of these features will be enabled';
    let tenseText = 'will be';

    if (this.flagValue) {
      introductionText = 'Enabled';
      tenseText = 'are';
    }

    return `${introductionText} for only you across all workspaces in your account. No other team members ${tenseText} affected.`;
  }

  @action
  async onSwitchChange() {
    if (this.flagValue && this.args.requiresFeedbackOnDisable === true) {
      this.isShowingFeedbackModal = true;
      return;
    }

    await this.toggleFlag.perform();
  }

  toggleFlag = dropTask(async () => {
    let toggled = !this.flagValue;

    if (this.auth.user == null) {
      this.alerts.error(
        'localization.show-live-language-warning.user-missing',
        'Unable to find current user. Refresh the page and try again.',
      );
      return;
    }

    await this.auth.user.updateUserFlag(this.args.key, toggled);

    this.analytics.track('experimental_features', 'toggled', { feature: this.args.title, toggled });
  });
}
